import React, { createContext, useContext, useCallback } from 'react';
import container from 'src/config/inversify-config';
import { DraftShortlistFormApiService } from 'src/api/draft-shortlist/draft-shortlist-api';

interface EnterpriseDraftShortlistContextType {
  tryRegisterDraftShortlist: (profileId: string, draftShortlistId: string) => Promise<{ success: boolean; draftShortlistId?: string }>;
  isRegistering: boolean;
}

const EnterpriseDraftShortlistContext = createContext<EnterpriseDraftShortlistContextType | undefined>(undefined);

export const EnterpriseDraftShortlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const draftShortlistFormService = container.get<DraftShortlistFormApiService>(DraftShortlistFormApiService);

  const [isRegistering, setIsRegistering] = React.useState(false);

  const tryRegisterDraftShortlist = useCallback(
    async (profileId: string, draftShortlistId: string) => {
      try {
        setIsRegistering(true);

        const r = await draftShortlistFormService.registerEnterprise(profileId, draftShortlistId);

        if (r.isSuccess) {
          return { success: true, draftShortlistId: draftShortlistId };
        }

        console.warn('Error registering enterprise from DraftShortlist', r.error);
        return { success: false };
      } catch (error) {
        console.error('Error registering enterprise from DraftShortlist', error);
        return { success: false };
      } finally {
        setIsRegistering(false);
      }
    },
    [draftShortlistFormService]
  );

  return (
    <EnterpriseDraftShortlistContext.Provider value={{ tryRegisterDraftShortlist, isRegistering }}>
      {children}
    </EnterpriseDraftShortlistContext.Provider>
  );
};

export const useEnterpriseDraftShortlist = () => {
  const context = useContext(EnterpriseDraftShortlistContext);
  if (!context) {
    throw new Error('useEnterpriseDraftShortlist must be used within an EnterpriseDraftShortlistProvider');
  }
  return context;
};
